import React, { useEffect, useState } from "react";
import "./main.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { IonIcon } from "@ionic/react";
import backgroundimg from "./backgroundimg.png";
import newLogo from "./new-logo.png";
import newLogoLight from "./new-logo-light.png";
import {
  logoGithub,
  logoTwitter,
  logoInstagram,
  logoLinkedin,
} from "ionicons/icons";
import AirtableList from "./Airtable";
import Projects from "./Projects";
import FramerWorks from "./FramerWorks";

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainPage />} />
      </Routes>
    </BrowserRouter>
  );
}

const MainPage = () => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") || "dark";
    }
    return "dark";
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navToggleActive, setNavToggleActive] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setNavToggleActive(!navToggleActive);
    document.body.style.overflow = mobileMenuOpen ? "" : "hidden";
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setNavToggleActive(false);
    document.body.style.overflow = "";
  };

  // Reveal on scroll
  useEffect(() => {
    const reveals = document.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) {
      reveals.forEach((el) => el.classList.add("visible"));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const skills = [
    { name: "CSS", color: "#264de4" },
    { name: "React", color: "#61DBFB" },
    { name: "JavaScript", color: "#f7df1e" },
    { name: "Shopify", color: "#90B945" },
    { name: "WordPress", color: "#1b769c" },
    { name: "Shopify Liquid", color: "#08089b" },
    { name: "TypeScript", color: "#3178c6" },
    { name: "Node.js", color: "#68a063" },
    { name: "Supabase", color: "#3ecf8e" },
  ];

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-inner">
          <a href="/" className="nav-logo">
            <img src={theme === "dark" ? newLogoLight : newLogo} alt="Gazi" className="nav-logo-img" />
            <span>Gazi</span>
          </a>

          <div className="nav-right">
            <div className="nav-links">
              <a href="#work" className="nav-link">Work</a>
              <a href="#framer" className="nav-link">Framer</a>
              <a href="#tools" className="nav-link">Tools</a>
              <a href="#about" className="nav-link">About</a>
              <a href="#contact" className="nav-link">Contact</a>
            </div>

            <div className="nav-actions">
              <button
                className="theme-toggle"
                onClick={toggleTheme}
                aria-label="Toggle theme"
              >
                <svg className="icon-moon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
                <svg className="icon-sun" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              </button>

              <a href="#contact" className="nav-cta">Let's Talk</a>
            </div>

            <button
              className={`nav-toggle ${navToggleActive ? "active" : ""}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`nav-mobile ${mobileMenuOpen ? "open" : ""}`}>
        <a href="#work" onClick={closeMobileMenu}>Work</a>
        <a href="#framer" onClick={closeMobileMenu}>Framer</a>
        <a href="#tools" onClick={closeMobileMenu}>Tools</a>
        <a href="#about" onClick={closeMobileMenu}>About</a>
        <a href="#contact" onClick={closeMobileMenu}>Contact</a>
      </div>

      {/* Hero */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            Open to collaborate
          </div>

          <h1 className="hero-heading">
            Hi, I'm Gazi.<br /><em>Full Stack Developer & Maker.</em>
          </h1>

          <p className="hero-sub">
            I build web applications, both frontend and backend. I enjoy involving people in the virtual world and am passionate about creating great digital experiences.
          </p>

          <div className="hero-buttons">
            <a href="#work" className="btn-primary">
              View my work
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
            <a href="#contact" className="btn-secondary">
              Get in touch
            </a>
          </div>

          <div className="hero-socials">
            <a
              href="https://github.com/gaaziyle"
              className="hero-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <IonIcon icon={logoGithub} />
            </a>
            <a
              href="https://twitter.com/gaaziyle"
              className="hero-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="X (Twitter)"
            >
              <IonIcon icon={logoTwitter} />
            </a>
            <a
              href="https://instagram.com/iamgaazicom"
              className="hero-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
            >
              <IonIcon icon={logoInstagram} />
            </a>
            <a
              href="https://www.linkedin.com/in/iamgaazi/"
              className="hero-social-link"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <IonIcon icon={logoLinkedin} />
            </a>
          </div>
        </div>
      </section>

      {/* Skills */}
      <section id="skills-section">
        <div className="section-inner reveal">
          <p className="section-label">Skills</p>
          <h2 className="section-heading">Technologies I Work With</h2>
          <div className="skills-grid">
            {skills.map((skill) => (
              <div key={skill.name} className="skill-pill">
                <span
                  className="skill-dot"
                  style={{ backgroundColor: skill.color }}
                ></span>
                {skill.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools (from Airtable) */}
      <section className="tools-section" id="tools">
        <div className="section-inner reveal">
          <p className="section-label">Toolkit</p>
          <h2 className="section-heading">Tools I Use</h2>
          <AirtableList />
        </div>
      </section>

      {/* Projects */}
      <section className="projects-section" id="work">
        <div className="section-inner">
          <div className="reveal">
            <p className="section-label">Selected Work</p>
            <h2 className="section-heading">My Projects</h2>
          </div>
          <Projects />
        </div>
      </section>

      {/* Framer Works */}
      <section className="framer-section" id="framer">
        <div className="section-inner">
          <div className="framer-header-row reveal">
            <div className="framer-header-left">
              <p className="section-label">Framer Ecosystem</p>
              <h2 className="section-heading">Framer Works</h2>
              <p className="framer-header-subtitle">
                Templates & components I built for the Framer community.
              </p>
            </div>
            <a
              href="https://www.framer.com/@gazi-efe/"
              target="_blank"
              rel="noopener noreferrer"
              className="framer-profile-badge"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2.5 0h11v5.5H8v5.5H2.5V0zm0 11h5.5v5L2.5 11zm5.5 0h5.5l-5.5 5v-5zM8 5.5h5.5L8 11V5.5z" />
              </svg>
              @gazi-efe
            </a>
          </div>
          <FramerWorks />
        </div>
      </section>

      {/* About */}
      <section className="about-section" id="about">
        <div className="section-inner">
          <div className="about-grid reveal">
            <div className="about-text">
              <p className="section-label">About</p>
              <h2>Hi, I'm Gazi.</h2>
              <p>
                Five years into development, I've built everything from Chrome
                extensions and Framer plugins to full-stack web apps with
                databases and APIs. I write code by hand when precision matters
                and use AI-assisted tools when speed does.
              </p>
              <p>
                I care about shipping things that work — clean frontends, solid
                backends, and products people actually use. Whether it's a
                complex project built from scratch or a rapid prototype brought
                to life with vibe coding, I get it done.
              </p>
            </div>
            <div className="about-photo">
              <img src={backgroundimg} alt="" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="contact-section" id="contact">
        <div className="section-inner reveal">
          <p className="section-label">Get in Touch</p>
          <h2 className="contact-heading">
            Have an idea?<br />Let's make <em>it real.</em>
          </h2>
          <a href="mailto:hi@iamgaazi.com" className="contact-email">
            hi@iamgaazi.com
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
          <p className="contact-sub">
            Open to freelance projects, partnerships, and co-building new products.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="footer-inner">
          <p>&copy; {new Date().getFullYear()} Gazi{" "}
            <a
              className="footer-link"
              href="https://pragwadev.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              pragwadev.com
            </a>
          </p>
          <div className="footer-links">
            <a
              className="footer-link"
              href="https://github.com/gaaziyle"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub
            </a>
            <a
              className="footer-link"
              href="https://twitter.com/gaaziyle"
              target="_blank"
              rel="noopener noreferrer"
            >
              X
            </a>
            <a
              className="footer-link"
              href="https://www.linkedin.com/in/iamgaazi/"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
